import oliviUrl from '../assets/sponsors/olivi.png';
import type { Sponsor } from '../track/sponsors';

/**
 * Sponsor wordmarks, painted once onto offscreen canvases and shared by both
 * renderers — the 2D one blits them into the world transform, the 3D one wraps
 * them in a `CanvasTexture`. One painter means a sponsor cannot look like two
 * different companies depending on which renderer is running.
 *
 * Nothing here runs at module load, so the headless tests can import the
 * placement code without ever reaching a `document`.
 */

/** Texture resolution. 128 px per metre of panel is crisp at any sane zoom. */
const PX_PER_M = 128;
const MAX_PX = 1024;

/** A painted panel, kept with the inputs that made it so a logo bitmap that
 *  arrives after the fact can repaint it in place rather than orphan it. */
interface CachedPanel {
  canvas: HTMLCanvasElement;
  sponsor: Sponsor;
  opts: PanelOptions;
}

const cache = new Map<string, CachedPanel>();

/** Logo bitmaps, background already keyed out and trimmed, by sponsor id. */
const bitmaps = new Map<string, HTMLCanvasElement>();
const loading = new Set<string>();
const listeners = new Set<() => void>();

/** Sponsors whose logo is a supplied image rather than drawn here. */
const MARK_IMAGES: Record<string, string | undefined> = { olivi: oliviUrl };

/**
 * Told when a logo bitmap has finished decoding and the cached panels have been
 * repainted with it.
 *
 * The 2D renderer needs nothing — it blits the same canvas objects every frame,
 * so a repaint shows up on its own. The 3D renderer has copied them into GPU
 * textures by then and has to be told to re-upload. Returns its own unsubscribe.
 */
export function onSponsorArtChanged(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Lift a logo off its flat background.
 *
 * The supplied artwork is a figure on an opaque near-black field, which on a
 * white panel would read as a dark tile with a logo in it. The field is one
 * flat colour, so keying it out against the corner pixel is exact, and the
 * result is then trimmed to the artwork's own bounds — without that, most of a
 * panel's logo box would be the padding that came with the file.
 */
function liftFromBackground(img: HTMLImageElement): HTMLCanvasElement | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return null;

  const src = document.createElement('canvas');
  src.width = w;
  src.height = h;
  const sx = src.getContext('2d', { willReadFrequently: true });
  if (!sx) return null;
  sx.drawImage(img, 0, 0);

  let data: ImageData;
  try {
    data = sx.getImageData(0, 0, w, h);
  } catch {
    // Tainted canvas: keep the artwork as it came rather than losing it.
    return src;
  }

  const px = data.data;
  const [br, bg, bb] = [px[0], px[1], px[2]];
  const tol = 60;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let i = 0; i < px.length; i += 4) {
    const near =
      Math.abs(px[i] - br) < tol && Math.abs(px[i + 1] - bg) < tol && Math.abs(px[i + 2] - bb) < tol;
    if (near) {
      px[i + 3] = 0;
      continue;
    }
    const p = i / 4;
    const x = p % w;
    const y = (p / w) | 0;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  sx.putImageData(data, 0, 0);
  if (maxX < minX || maxY < minY) return src;

  const pad = 2;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad);
  maxY = Math.min(h - 1, maxY + pad);

  const out = document.createElement('canvas');
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out.getContext('2d')?.drawImage(src, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

/** Start decoding a sponsor's logo, once, and repaint its panels when it lands. */
function requestBitmap(id: string): void {
  const url = MARK_IMAGES[id];
  if (!url || loading.has(id) || bitmaps.has(id) || typeof Image === 'undefined') return;
  loading.add(id);
  const img = new Image();
  img.decoding = 'async';
  img.onload = () => {
    const lifted = liftFromBackground(img);
    if (!lifted) return;
    bitmaps.set(id, lifted);
    for (const entry of cache.values()) {
      if (entry.sponsor.id === id) paintPanel(entry.canvas, entry.sponsor, entry.opts);
    }
    for (const fn of listeners) fn();
  };
  img.src = url;
}

/**
 * A sponsor's logo mark, drawn into a square box at (x, y) of side `s`.
 *
 * Where a sponsor has supplied artwork this draws that; until it has decoded —
 * and the panels are painted synchronously, the first of them before any image
 * could possibly have loaded — it draws the vector version below, so a board is
 * never blank and never waits. A mark is optional: most sponsors are a wordmark
 * alone.
 */
type Mark = (cx: CanvasRenderingContext2D, x: number, y: number, s: number, sponsor: Sponsor) => void;

/** The supplied artwork, fitted into the box without distorting its aspect. */
function drawBitmapMark(
  cx: CanvasRenderingContext2D,
  bmp: HTMLCanvasElement,
  x: number,
  y: number,
  s: number,
): void {
  const scale = Math.min(s / bmp.width, s / bmp.height);
  const w = bmp.width * scale;
  const h = bmp.height * scale;
  cx.drawImage(bmp, x + (s - w) / 2, y + (s - h) / 2, w, h);
}

/**
 * olivi.co's mark: a bob-haired figure with a heart in a speech bubble, in the
 * same indigo linework as the wordmark. White fill rather than the panel's own
 * background, so it still reads as a figure when the panel is transparent and
 * the paint underneath it is the sponsor's colour.
 */
function oliviMark(cx: CanvasRenderingContext2D, x: number, y: number, s: number, sponsor: Sponsor): void {
  const at = (ux: number, uy: number): [number, number] => [x + ux * s, y + uy * s];

  cx.save();
  cx.lineJoin = 'round';
  cx.lineCap = 'round';
  cx.strokeStyle = sponsor.fg;
  cx.fillStyle = '#ffffff';
  cx.lineWidth = Math.max(1, s * 0.042);

  // The hair, as one mass. It is the whole of the silhouette, so it is drawn as
  // a bob — flaring past the ears and tucking into two tips — rather than the
  // ellipse it would be easy to settle for, which reads as a smiley face.
  const curve = (pts: number[][]): void => {
    cx.beginPath();
    cx.moveTo(...at(pts[0][0], pts[0][1]));
    for (let i = 1; i < pts.length; i += 3) {
      cx.bezierCurveTo(
        ...at(pts[i][0], pts[i][1]),
        ...at(pts[i + 1][0], pts[i + 1][1]),
        ...at(pts[i + 2][0], pts[i + 2][1]),
      );
    }
    cx.closePath();
  };
  curve([
    [0.58, 0.28],
    [0.80, 0.28], [0.95, 0.48], [0.93, 0.72],
    [0.92, 0.86], [0.86, 0.93], [0.80, 0.85],
    [0.74, 0.97], [0.42, 0.97], [0.36, 0.85],
    [0.30, 0.93], [0.24, 0.86], [0.23, 0.72],
    [0.21, 0.48], [0.36, 0.28], [0.58, 0.28],
  ]);
  cx.fill();
  cx.stroke();

  // A parting and a couple of strands over the crown.
  cx.beginPath();
  for (const [ax, ay, cx2, cy2, bx, by] of [
    [0.34, 0.62, 0.34, 0.42, 0.52, 0.33],
    [0.48, 0.58, 0.54, 0.42, 0.70, 0.36],
    [0.78, 0.42, 0.86, 0.52, 0.85, 0.64],
  ]) {
    cx.moveTo(...at(ax, ay));
    cx.quadraticCurveTo(...at(cx2, cy2), ...at(bx, by));
  }
  cx.stroke();

  // Eyes closed in a smile, and a small mouth under them.
  cx.beginPath();
  for (const ex of [0.47, 0.71]) {
    cx.moveTo(...at(ex - 0.07, 0.72));
    cx.quadraticCurveTo(...at(ex, 0.63), ...at(ex + 0.07, 0.72));
  }
  cx.moveTo(...at(0.52, 0.79));
  cx.quadraticCurveTo(...at(0.59, 0.86), ...at(0.66, 0.79));
  cx.stroke();

  // Speech bubble, its tail a single dot, with a heart in it.
  cx.beginPath();
  cx.ellipse(...at(0.18, 0.19), s * 0.17, s * 0.15, 0, 0, Math.PI * 2);
  cx.fill();
  cx.stroke();
  cx.beginPath();
  cx.ellipse(...at(0.3, 0.36), s * 0.035, s * 0.035, 0, 0, Math.PI * 2);
  cx.fill();
  cx.stroke();

  const hs = s * 0.085;
  const [hx, hy] = at(0.18, 0.19);
  cx.beginPath();
  cx.moveTo(hx, hy + hs * 0.75);
  cx.bezierCurveTo(hx - hs * 1.5, hy - hs * 0.2, hx - hs * 0.7, hy - hs * 1.2, hx, hy - hs * 0.3);
  cx.bezierCurveTo(hx + hs * 0.7, hy - hs * 1.2, hx + hs * 1.5, hy - hs * 0.2, hx, hy + hs * 0.75);
  cx.stroke();

  cx.restore();
}

const VECTOR_MARKS: Record<string, Mark | undefined> = { olivi: oliviMark };

/** Whichever version of a sponsor's mark is available right now, or none. */
function markFor(sponsor: Sponsor): Mark | undefined {
  const vector = VECTOR_MARKS[sponsor.id];
  if (!MARK_IMAGES[sponsor.id]) return vector;
  requestBitmap(sponsor.id);
  const bmp = bitmaps.get(sponsor.id);
  return bmp ? (cx, x, y, s) => drawBitmapMark(cx, bmp, x, y, s) : vector;
}

function fontOf(sponsor: Sponsor, size: number): string {
  return `${sponsor.italic ? 'italic ' : ''}${sponsor.weight} ${size}px ${sponsor.family}`;
}

/**
 * Fill style for a wordmark: a vertical chrome gradient for a `metallic`
 * sponsor, the flat `fg` colour for everyone else.
 *
 * Bands rather than a single highlight — near-white, mid-grey, a dark seam,
 * mid-grey, near-white again — because a smooth two-stop fade reads as a flat
 * light-to-dark tint, not metal. The banding is what a real chrome decal does
 * under one light source, and it is the cheapest way to sell "shiny" on a
 * canvas with no actual reflection to render.
 */
function textFillStyle(
  cx: CanvasRenderingContext2D,
  sponsor: Sponsor,
  top: number,
  bottom: number,
): string | CanvasGradient {
  if (!sponsor.metallic) return sponsor.fg;
  const grad = cx.createLinearGradient(0, top, 0, bottom);
  grad.addColorStop(0, '#f6f8fa');
  grad.addColorStop(0.24, '#c9ced3');
  grad.addColorStop(0.47, '#82878d');
  grad.addColorStop(0.5, '#5f6469');
  grad.addColorStop(0.53, '#8d9298');
  grad.addColorStop(0.78, '#d8dbde');
  grad.addColorStop(1, '#f2f4f6');
  return grad;
}

function withTracking(cx: CanvasRenderingContext2D, sponsor: Sponsor, size: number): void {
  const em = sponsor.tracking ?? 0;
  // letterSpacing is recent; skipping it costs a little polish, never a frame.
  if ('letterSpacing' in cx) {
    (cx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
      `${(em * size).toFixed(2)}px`;
  }
}

/**
 * Largest size at which the wordmark fits the box, found by measuring once and
 * scaling — both the glyph widths and the letter spacing are linear in font
 * size, so a second measure only ever confirms the first.
 *
 * The spacing has to be on the context *before* the measure. Measuring without
 * it and applying it afterwards is what made "Yakutia Motor Corp" lose its
 * first and last letter off the ends of a barrier panel.
 *
 * Fits to the *ink* — `actualBoundingBox*` — rather than `.width`, the glyph
 * advance. An italic wordmark's letterforms lean past their own advance box,
 * so "Aldan Tyres" was clipping its own trailing edge on the ground and
 * barrier panels that have no padding to absorb it. A `metallic` sponsor's
 * stroke sits half outside the fill outline on every side and is added on
 * top for the same reason. `textAlign`/`textBaseline` are set here to match
 * how the text is actually drawn, because the measurement is relative to them.
 * A 3% margin below that soaks up the last bit of rounding rather than
 * sizing exactly to the edge.
 */
function fitText(
  cx: CanvasRenderingContext2D,
  sponsor: Sponsor,
  text: string,
  maxW: number,
  maxH: number,
): number {
  const probe = 100;
  cx.font = fontOf(sponsor, probe);
  withTracking(cx, sponsor, probe);
  cx.textAlign = 'left';
  cx.textBaseline = 'middle';
  const m = cx.measureText(text);
  const strokeInset = sponsor.metallic ? probe * 0.05 : 0;
  const w = (Number.isFinite(m.actualBoundingBoxLeft) && Number.isFinite(m.actualBoundingBoxRight)
    ? m.actualBoundingBoxLeft + m.actualBoundingBoxRight
    : m.width) + strokeInset || probe;
  const h = (Number.isFinite(m.actualBoundingBoxAscent) && Number.isFinite(m.actualBoundingBoxDescent)
    ? m.actualBoundingBoxAscent + m.actualBoundingBoxDescent
    : probe * 0.72) + strokeInset;
  return Math.max(6, Math.min((maxW / w) * probe, (maxH / h) * probe) * 0.97);
}

export interface PanelOptions {
  /** Paint the sponsor's background and edge stripe. Off for ground paint,
   *  where the painted area is already geometry in the sponsor's colour. */
  background?: boolean;
  /** Panel width and height in metres — sets the aspect and the resolution. */
  wMetres: number;
  hMetres: number;
}

/**
 * A sponsor panel of the given real-world size. Cached: a circuit draws the
 * same four wordmarks a few hundred times a frame and must not repaint any of
 * them.
 */
export function sponsorPanel(sponsor: Sponsor, opts: PanelOptions): HTMLCanvasElement {
  const bg = opts.background !== false;
  const key = `${sponsor.id}|${opts.wMetres.toFixed(2)}x${opts.hMetres.toFixed(2)}|${bg ? 'p' : 't'}`;
  const hit = cache.get(key);
  if (hit) return hit.canvas;

  const scale = Math.min(PX_PER_M, MAX_PX / Math.max(opts.wMetres, opts.hMetres));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(8, Math.round(opts.wMetres * scale));
  canvas.height = Math.max(8, Math.round(opts.hMetres * scale));
  cache.set(key, { canvas, sponsor, opts });
  paintPanel(canvas, sponsor, opts);
  return canvas;
}

/** Paints a panel into a canvas it already owns. Separate from the cache so a
 *  logo that decodes later can be painted into the very canvas the renderers
 *  are already holding, rather than a replacement they would never see. */
function paintPanel(cnv: HTMLCanvasElement, sponsor: Sponsor, opts: PanelOptions): void {
  const bg = opts.background !== false;
  const w = cnv.width;
  const h = cnv.height;
  const cx = cnv.getContext('2d');
  if (!cx) return;
  cx.clearRect(0, 0, w, h);

  const trim = Math.max(1, Math.round(Math.min(w, h) * 0.035));
  if (bg) {
    cx.fillStyle = sponsor.bg;
    cx.fillRect(0, 0, w, h);
    cx.strokeStyle = sponsor.trim;
    cx.lineWidth = trim;
    cx.strokeRect(trim / 2, trim / 2, w - trim, h - trim);
  }

  const text = sponsor.upper ? sponsor.name.toUpperCase() : sponsor.name;
  // A background panel keeps the wordmark off its own frame; a ground or
  // barrier label has no frame to keep off, but still wants a sliver of
  // margin — without it, `fitText`'s own approximation error and a glyph's
  // antialiased fringe were enough to touch the texture edge, which reads as
  // a clipped letter on a self-crossing circuit's tightest labels.
  const padX = trim * (bg ? 3 : 1);
  const padY = trim * (bg ? 3 : 1);
  const boxW = w - padX * 2;
  const boxH = h - padY * 2;

  // A mark takes a square of the panel's height on the left, capped so it can
  // never crowd out the name on a long, low barrier panel.
  const mark = markFor(sponsor);
  const markSize = mark ? Math.min(boxH, boxW * 0.24) : 0;
  const gap = markSize * 0.22;

  const size = fitText(cx, sponsor, text, boxW - markSize - gap, boxH);
  cx.font = fontOf(sponsor, size);
  withTracking(cx, sponsor, size);
  // Tracking pads the trailing side only, so discount half of it from the
  // measured width to keep the wordmark optically centred.
  const drift = ((sponsor.tracking ?? 0) * size) / 2;
  const textW = cx.measureText(text).width - drift;

  // Mark and name are centred as one unit, not each in its own half.
  const left = (w - (markSize + gap + textW)) / 2;
  if (mark) mark(cx, left, (h - markSize) / 2, markSize, sponsor);

  cx.font = fontOf(sponsor, size);
  withTracking(cx, sponsor, size);
  cx.textAlign = 'left';
  cx.textBaseline = 'middle';
  const textX = left + markSize + gap;

  // `middle` centres on the font's em box, which reserves room for a
  // descender a wordmark in capitals never uses — drawing at a flat `h / 2`
  // left the ink sitting noticeably above centre, close enough to the top on
  // a tightly-fitted ground or barrier label to clip against it. Re-measuring
  // at the final size gives the real ink extents, so the glyphs can be
  // shifted down onto true visual centre instead.
  const vm = cx.measureText(text);
  const ascent = Number.isFinite(vm.actualBoundingBoxAscent) ? vm.actualBoundingBoxAscent : size * 0.72;
  const descent = Number.isFinite(vm.actualBoundingBoxDescent) ? vm.actualBoundingBoxDescent : 0;
  const textY = h / 2 + (ascent - descent) / 2;

  if (sponsor.metallic) {
    // A dark seam under the fill is what makes the chrome read as engraved
    // rather than printed — without it the gradient just looks like a flat
    // grey wordmark on a red panel.
    cx.strokeStyle = 'rgba(20, 12, 12, 0.55)';
    cx.lineWidth = Math.max(1, size * 0.05);
    cx.lineJoin = 'round';
    cx.strokeText(text, textX, textY);
  }
  cx.fillStyle = textFillStyle(cx, sponsor, textY - ascent, textY + descent);
  cx.fillText(text, textX, textY);
}

/**
 * World angle at which a panel's text reads left-to-right on screen.
 *
 * Ads face the driver, so their natural angle is the track's tangent — which in
 * a fixed north-up overhead leaves half the circuit's signage upside down. The
 * 2D renderer flips those by half a turn: the panel is in the same place and
 * says the same thing, it is just legible from where the 2D camera actually
 * sits. The 3D renderer needs none of this, because there the driver is in the
 * world and the tangent already points at them.
 */
export function readableAngle(tx: number, ty: number): number {
  const a = Math.atan2(ty, tx);
  return Math.abs(a) > Math.PI / 2 ? a + Math.PI : a;
}
